/**
 * @fileoverview Observatory Detail Page Wrapper
 * Wraps individual observatory dashboards with consistent layout.
 * Handles sub-routes for USD Core, FX Stress, Risk & Credit panels.
 * 
 * @module pages/observatory
 *
 * CONTRACT:
 * - renderObservatoryPage(id, subPage, timeRange) returns observatory wrapper
 * - For Dollar Liquidity: uses custom DollarLiquidityDashboard
 * - Sub-pages: usd-core, fx-stress, risk-credit, methodology
 */

import { el, renderDashboard } from "../shared/components/simpleRender.js";
import { 
  dollarLiquidityManifest, 
  buildDollarLiquidityViewModel 
} from "../observatories/index.js";
import { 
  renderDollarLiquidityDashboard, 
  initDollarLiquidityDashboard,
  cleanupDollarLiquidityDashboard,
  renderUSDCorePage,
  initUSDCorePage,
  renderFXStressPage,
  initFXStressPage,
  renderRiskCreditPage,
  initRiskCreditPage
} from "../observatories/dollar_liquidity/DollarLiquidityDashboard.js";

/**
 * Tracks the currently active observatory for cleanup.
 * @type {string|null}
 */
let currentObservatoryId = null;

/**
 * Registry of observatory builders.
 */
const OBSERVATORY_REGISTRY = {
  dollar_liquidity: {
    manifest: dollarLiquidityManifest,
    buildViewModel: buildDollarLiquidityViewModel,
    // Custom renderers for Dollar Liquidity and sub-pages
    pages: {
      overview: {
        render: renderDollarLiquidityDashboard,
        init: initDollarLiquidityDashboard
      },
      "usd-core": {
        render: renderUSDCorePage,
        init: initUSDCorePage
      },
      "fx-stress": {
        render: renderFXStressPage,
        init: initFXStressPage
      },
      "risk-credit": {
        render: renderRiskCreditPage,
        init: initRiskCreditPage
      },
      methodology: {
        render: renderMethodologyPage,
        init: null
      }
    }
  },
};

/**
 * Renders methodology page for Dollar Liquidity Observatory
 */
function renderMethodologyPage() {
  return el("div", { className: "dollar-liquidity-observatory methodology-page" }, [
    el("header", { className: "obs-header" }, [
      el("div", { className: "obs-header-inner" }, [
        el("div", { className: "obs-header-left" }, [
          el("a", { href: "#/observatories/dollar_liquidity", className: "obs-logo" }, [
            el("div", { className: "logo-dot" }, []),
            el("span", { className: "logo-text" }, ["Dollar Liquidity Observatory"])
          ])
        ]),
        el("nav", { className: "obs-nav" }, [
          el("a", { href: "#/observatories/dollar_liquidity", className: "obs-nav-link", "data-tab": "overview" }, ["Overview"]),
          el("a", { href: "#/observatories/dollar_liquidity/usd-core", className: "obs-nav-link", "data-tab": "usd-core" }, ["USD Core"]),
          el("a", { href: "#/observatories/dollar_liquidity/fx-stress", className: "obs-nav-link", "data-tab": "fx-stress" }, ["FX Stress"]),
          el("a", { href: "#/observatories/dollar_liquidity/risk-credit", className: "obs-nav-link", "data-tab": "risk-credit" }, ["Risk & Credit"]),
          el("a", { href: "#/observatories/dollar_liquidity/methodology", className: "obs-nav-link active", "data-tab": "methodology" }, ["Methodology"])
        ])
      ])
    ]),
    el("main", { className: "page-content methodology-content" }, [
      el("article", { className: "methodology-article" }, [
        el("h1", {}, ["Dollar Stress Index — Methodology"]),
        
        el("section", { className: "methodology-section" }, [
          el("h2", {}, ["Overview"]),
          el("p", {}, [
            "The Dollar Stress Index (DSI) is a proprietary composite indicator designed to quantify real-time stress in global USD funding markets. The index synthesizes signals across four key dimensions: dollar momentum, carry-adjusted FX pressure, volatility regime classification, and credit market dislocation."
          ]),
          el("p", {}, [
            "Unlike simple dollar strength measures, the DSI captures the funding stress dynamic — the scramble for USD liquidity that occurs when global capital seeks safety in dollar-denominated assets, often at the expense of EM currencies, high-yield credit, and risk assets broadly."
          ])
        ]),
        
        el("section", { className: "methodology-section" }, [
          el("h2", {}, ["Component Signals"]),
          
          el("div", { className: "signal-card" }, [
            el("h3", {}, ["DXY Momentum ", el("span", { className: "signal-weight" }, ["(30% weight)"])]),
            el("p", {}, [
              "Rate-of-change analysis on the DXY index using adaptive lookback windows (5d, 21d, 63d). The signal incorporates both level and acceleration components, with higher readings indicating rapid dollar appreciation — a hallmark of funding stress episodes."
            ])
          ]),
          
          el("div", { className: "signal-card" }, [
            el("h3", {}, ["JPY Carry Stress ", el("span", { className: "signal-weight" }, ["(25% weight)"])]),
            el("p", {}, [
              "USD/JPY adjusted for rate differentials and vol-carry. Monitors breakdowns in the yen carry trade, which historically precedes broader risk-off cascades. Incorporates BOJ intervention probability estimates and YCC band stress indicators."
            ])
          ]),
          
          el("div", { className: "signal-card" }, [
            el("h3", {}, ["Vol Regime ", el("span", { className: "signal-weight" }, ["(25% weight)"])]),
            el("p", {}, [
              "Multi-tenor VIX term structure analysis combined with realized/implied vol spreads. Classifies current regime as Low Vol, Transition, Elevated, or Crisis using hidden Markov model state probabilities calibrated to historical stress episodes."
            ])
          ]),
          
          el("div", { className: "signal-card" }, [
            el("h3", {}, ["Credit Z-Score ", el("span", { className: "signal-weight" }, ["(20% weight)"])]),
            el("p", {}, [
              "High-yield spread deviation from rolling mean, normalized by historical volatility. Tracks credit market liquidity via HYG/LQD ratio dynamics and CDX spread movements. Credit stress typically lags FX stress by 1-3 sessions, providing confirmation signal."
            ])
          ])
        ]),
        
        el("section", { className: "methodology-section" }, [
          el("h2", {}, ["Regime-Adaptive Weighting"]),
          el("p", {}, [
            "Component weights are not static. The DSI employs a dynamic weighting scheme based on rolling 60-day correlation structure between signal components. During periods of high cross-correlation (typical of stress regimes), the model increases weight on leading indicators (DXY Mom, Vol Regime) while reducing weight on lagging confirmations (Credit Z)."
          ]),
          el("p", {}, [
            "Weight adjustments are bounded (±5% from base) to prevent overfitting and maintain interpretability. The correlation matrix is computed using exponentially-weighted observations with λ = 0.94."
          ])
        ]),
        
        el("section", { className: "methodology-section" }, [
          el("h2", {}, ["Historical Normalization"]),
          el("p", {}, [
            "Raw signal values are normalized against a reference distribution constructed from five major USD stress episodes:"
          ]),
          el("ul", { className: "episode-list" }, [
            el("li", {}, [el("strong", {}, ["2008 GFC"]), " — Global banking crisis, peak DSI: 94"]),
            el("li", {}, [el("strong", {}, ["2011 EU Sovereign"]), " — Eurozone fragmentation risk, peak DSI: 78"]),
            el("li", {}, [el("strong", {}, ["2015-16 China/EM"]), " — CNY devaluation cascade, peak DSI: 71"]),
            el("li", {}, [el("strong", {}, ["2020 COVID"]), " — March liquidity crisis, peak DSI: 89"]),
            el("li", {}, [el("strong", {}, ["2022 Energy/Rates"]), " — Fed tightening + energy shock, peak DSI: 82"])
          ]),
          el("p", {}, [
            "This normalization ensures the 0-100 scale maintains consistent interpretation across market regimes."
          ])
        ]),
        
        el("section", { className: "methodology-section" }, [
          el("h2", {}, ["Interpretation Guide"]),
          el("div", { className: "interpretation-grid" }, [
            el("div", { className: "interp-item interp-low" }, [
              el("span", { className: "interp-range" }, ["0-24"]),
              el("span", { className: "interp-label" }, ["Low Stress"]),
              el("p", {}, ["Benign USD funding conditions. Risk-on environment, carry trades performing."])
            ]),
            el("div", { className: "interp-item interp-moderate" }, [
              el("span", { className: "interp-range" }, ["25-44"]),
              el("span", { className: "interp-label" }, ["Moderate"]),
              el("p", {}, ["Normal fluctuations. Some cross-currents but no systemic pressure."])
            ]),
            el("div", { className: "interp-item interp-elevated" }, [
              el("span", { className: "interp-range" }, ["45-64"]),
              el("span", { className: "interp-label" }, ["Elevated"]),
              el("p", {}, ["Heightened vigilance warranted. Early signs of funding pressure emerging."])
            ]),
            el("div", { className: "interp-item interp-high" }, [
              el("span", { className: "interp-range" }, ["65-79"]),
              el("span", { className: "interp-label" }, ["High Stress"]),
              el("p", {}, ["Significant funding stress. Historical analog: early stages of past crises."])
            ]),
            el("div", { className: "interp-item interp-extreme" }, [
              el("span", { className: "interp-range" }, ["80-100"]),
              el("span", { className: "interp-label" }, ["Extreme"]),
              el("p", {}, ["Crisis-level conditions. Comparable to peak 2008/2020 stress readings."])
            ])
          ])
        ]),
        
        el("section", { className: "methodology-section" }, [
          el("h2", {}, ["Data Sources"]),
          el("div", { className: "data-sources-grid" }, [
            el("div", { className: "source-card" }, [
              el("h4", {}, ["TradingView"]),
              el("p", {}, ["Real-time FX, ETF, and index data via embedded widgets."])
            ]),
            el("div", { className: "source-card" }, [
              el("h4", {}, ["FRED API"]),
              el("p", {}, ["Federal Reserve economic data for macro indicators."])
            ]),
            el("div", { className: "source-card" }, [
              el("h4", {}, ["Yahoo Finance"]),
              el("p", {}, ["ETF pricing and historical data backup."])
            ])
          ])
        ]),
        
        el("section", { className: "methodology-section disclaimer-section" }, [
          el("h2", {}, ["Limitations & Disclaimers"]),
          el("p", {}, [
            "The DSI is a research tool designed to contextualize market conditions, not a trading signal. Key limitations include:"
          ]),
          el("ul", {}, [
            el("li", {}, ["Backward-looking normalization may underestimate novel stress regimes"]),
            el("li", {}, ["Component weights optimized on historical data may not persist"]),
            el("li", {}, ["Intraday readings subject to noise; daily closes more reliable"]),
            el("li", {}, ["Does not incorporate central bank intervention timing or magnitude"])
          ]),
          el("p", { className: "disclaimer" }, [
            el("strong", {}, ["Not investment advice."]),
            " The Dollar Liquidity Observatory provides information for educational and research purposes only. Past performance of any indicator does not guarantee future results. Always conduct independent analysis and consult qualified professionals before making investment decisions."
          ])
        ]),
        
        el("div", { className: "methodology-cta" }, [
          el("a", { href: "#/observatories/dollar_liquidity", className: "btn-primary" }, ["← Back to Dashboard"])
        ])
      ])
    ])
  ]);
}

/**
 * Renders the not found state for an unknown observatory.
 */
function renderNotFoundState(id) {
  return el("div", { className: "cap-observatory cap-observatory--not-found" }, [
    el("h1", { className: "cap-page-title" }, ["Observatory Not Found"]),
    el("p", { className: "cap-page-subtitle" }, [
      `The observatory "${id}" does not exist or is not yet available.`
    ]),
    el("a", { href: "#/observatories", className: "cap-button cap-button--primary" }, [
      "← View Available Observatories"
    ]),
  ]);
}

/**
 * Renders an observatory page.
 * 
 * @param {string} id - Observatory ID (e.g., "dollar_liquidity")
 * @param {string} subPage - Sub-page ID (e.g., "usd-core", "fx-stress")
 * @param {string} timeRange - Time range for data (e.g., "1Y")
 * @returns {Promise<HTMLElement>} Observatory page element
 */
export async function renderObservatoryPage(id, subPage = "overview", timeRange) {
  const registry = OBSERVATORY_REGISTRY[id];

  // Handle unknown observatory
  if (!registry) {
    console.warn(`[Observatory] Unknown observatory: ${id}`);
    return renderNotFoundState(id);
  }

  const { manifest, pages } = registry;

  // Get the appropriate page renderer
  const pageConfig = pages[subPage] || pages.overview;
  
  if (pageConfig && pageConfig.render) {
    console.log(`[Observatory] Rendering ${manifest.name} - ${subPage}`);
    
    // Track current observatory for cleanup
    currentObservatoryId = id;
    
    const dashboard = pageConfig.render();
    
    // Schedule initialization after DOM insertion
    if (pageConfig.init) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          pageConfig.init();
        });
      });
    }
    
    return dashboard;
  }

  // Fallback to generic renderer for other observatories
  console.log(`[Observatory] Loading ${manifest.name} with generic renderer...`);
  
  const container = el("div", { className: "cap-observatory" }, [
    el("header", { className: "cap-observatory-header" }, [
      el("a", { href: "#/observatories", className: "cap-observatory-back-link" }, [
        "← Observatories"
      ]),
      el("h1", { className: "cap-page-title" }, [manifest.name]),
      el("p", { className: "cap-page-subtitle" }, [manifest.shortDescription]),
    ]),
    el("div", { className: "cap-observatory-body" }, [
      el("p", { className: "text-faint" }, ["Loading dashboard..."])
    ]),
  ]);

  return container;
}

/**
 * Cleans up the current observatory's intervals and resources.
 * Call this when navigating away from an observatory page.
 */
export function cleanupObservatory() {
  if (currentObservatoryId === "dollar_liquidity") {
    cleanupDollarLiquidityDashboard();
  }
  currentObservatoryId = null;
}

export { OBSERVATORY_REGISTRY };
